import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { format } from 'date-fns';
import { API_BASE_URL } from '../constants';
import { storageService } from '../services';

interface EatingLyingDataPoint {
  three_hr: string;
  Lying_sum_3hr: number;
  Eating_sum_3hr: number;
}

interface EatingLyingChartProps {
  cowId: string;
  sensorId: string;
  startDate?: Date;
  endDate?: Date;
}

const EatingLyingChart: React.FC<EatingLyingChartProps> = ({
  cowId,
  sensorId,
  startDate,
  endDate,
}) => {
  const [data, setData] = useState<EatingLyingDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchEatingLyingData();
  }, [cowId, startDate, endDate]);

  const fetchEatingLyingData = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = storageService.getAuthToken();
      const params = new URLSearchParams();

      if (startDate) {
        params.append('startDate', format(startDate, 'yyyy-MM-dd HH:mm:ss'));
      }
      if (endDate) {
        params.append('endDate', format(endDate, 'yyyy-MM-dd HH:mm:ss'));
      }

      const response = await fetch(
        `${API_BASE_URL}/behaviors/cow/${cowId}/eating-lying?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch eating/lying data');
      }

      const result = await response.json();
      setData(result.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const formatXAxis = (tickItem: string) => {
    try {
      return format(new Date(tickItem), 'MM/dd HH:mm');
    } catch {
      return tickItem;
    }
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-gray-300 p-3 rounded shadow-lg">
          <p className="font-semibold text-sm mb-2">
            {format(new Date(label), 'MMM dd, yyyy HH:mm')}
          </p>
          {payload.map((entry: any, index: number) => {
            const value = typeof entry.value === 'number' ? entry.value : Number(entry.value) || 0;
            return (
              <p key={index} className="text-xs" style={{ color: entry.color }}>
                {entry.name}: {value.toFixed(2)} นาที
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchEatingLyingData}
            className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
          >
            ลองใหม่
          </button>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-gray-500">ไม่พบข้อมูลพฤติกรรมการกินและนอน</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            กราฟพฤติกรรมการกินและนอน (รวมทุก 3 ชั่วโมง)
          </h3>
          <p className="text-sm text-gray-600">Sensor ID: {sensorId}</p>
        </div>
        <div className="text-sm text-gray-500">
          ข้อมูล {data.length} รายการ
        </div>
      </div>

      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={data}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} />
          <XAxis
            dataKey="three_hr"
            tickFormatter={formatXAxis}
            stroke="#6b7280"
            style={{ fontSize: '12px' }}
          />
          <YAxis
            stroke="#6b7280"
            style={{ fontSize: '12px' }}
            label={{ value: 'นาที', angle: -90, position: 'insideLeft' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ fontSize: '12px' }}
            iconType="square"
          />

          {/* Lying - สีฟ้า */}
          <Bar
            dataKey="Lying_sum_3hr"
            fill="#3b82f6"
            name="การนอน"
          />

          {/* Eating - สีเขียว */}
          <Bar
            dataKey="Eating_sum_3hr"
            fill="#10b981"
            name="การกิน"
          />
        </BarChart>
      </ResponsiveContainer>

      {/* Legend Description */}
      <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-blue-500"></div>
          <span>การนอน (Lying) - เวลารวมต่อ 3 ชั่วโมง</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-500"></div>
          <span>การกิน (Eating) - เวลารวมต่อ 3 ชั่วโมง</span>
        </div>
      </div>

      {/* Info Note */}
      <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">
          คำอธิบาย
        </h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>
            • กราฟแสดงเวลาที่โคใช้ในการกินและนอน รวมกันทุก 3 ชั่วโมง
          </li>
          <li>
            • ข้อมูลคำนวณจากระบบ sensor ติดตามพฤติกรรม
          </li>
          <li>
            • หน่วยเป็นนาที (minutes)
          </li>
        </ul>
      </div>
    </div>
  );
};

export default EatingLyingChart;
